import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { ToasterService } from '@lib/components/toaster/toaster.service';
import { AuthService } from '@lib/services';
import { ErrorHandlerService } from '@lib/services/error/error-handler.service';
import { GlobalErrorService } from '@lib/services/global-error';
import { catchError, EMPTY, throwError } from 'rxjs';

/**
 * Interceptor that handles server errors globally.
 * Extracts specific error messages from backend responses and displays them via toaster.
 * Components can still handle their own errors by catching them in their subscribe blocks.
 */
export const serverErrorInterceptor: HttpInterceptorFn = (request, next) => {
    const authService = inject(AuthService);
    const toasterService = inject(ToasterService);
    const errorHandlerService = inject(ErrorHandlerService);
    const globalErrorService = inject(GlobalErrorService);

    return next(request).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 0 || error.error instanceof ErrorEvent) {
                globalErrorService.storeFailedRequest(request, next);
                globalErrorService.showError(error);
                return throwError(() => error);
            }

            if (error.status === 409) {
                if (request.url.includes('/payment/init')) {
                    return throwError(() => error);
                }
                return EMPTY;
            }

            if (error.status === 401 || error.status === 403) {
                if (request.url.includes('/auth/refresh-token')) {
                    errorHandlerService.handleErrorWithToaster(error, toasterService, {
                        context: 'global',
                        defaultMessage: error.error.message,
                        fallbackToApiMessage: true,
                        showFriendlyMessage: true,
                    });
                    return throwError(() => error);
                }

                if (error.status === 403) {
                    // Authenticated but not authorized for this specific resource -
                    // surface the backend's message via toaster, keep the session intact.
                    errorHandlerService.handleErrorWithToaster(error, toasterService, {
                        context: 'global',
                        defaultMessage: error.error?.message,
                        fallbackToApiMessage: true,
                        showFriendlyMessage: false,
                    });
                    return throwError(() => error);
                }

                authService.logout();
                globalErrorService.showError(error);
                return throwError(() => error);
            }

            if (error.status >= 500) {
                globalErrorService.storeFailedRequest(request, next);
                globalErrorService.showError(error);
                return throwError(() => error);
            }

            if (error.status >= 400 && error.status < 500) {
                errorHandlerService.handleErrorWithToaster(error, toasterService, {
                    context: 'global',
                    defaultMessage: 'An error occurred. Please try again.',
                    fallbackToApiMessage: true,
                    showFriendlyMessage: true,
                });
            }

            return throwError(() => error);
        }),
    );
};
